import React, { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { bool, shape, string, arrayOf } from "prop-types";
import { Link } from "@quintype/components";

import { ProfileCard, Span } from "../../atoms";
import { useTrackWidgetImpression, getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";
import { SvgIconHandler } from "../../atoms/SvgIconHandler";
import ArrowDashIcon from "../../atoms/Arrow-DashIcon";

import "./FourProfileCards.m.css";

const FourProfileCards = ({
  options,
  customStyleName,
  showRanking,
  heading,
  logoLink,
  buttonText,
  buttonLink,
  year = "",
  storyPage = false,
  cdnImage,
  story = {},
  config = {},
  trendingPeople = false,
}) => {
  if (options.length === 0) return null;

  const cardsRef = useRef(null);
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const pageType = get(qtState, ["pageType"], "home-page");
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(true);
  const linkCategory = year ? `${heading} ${year}` : heading;

  const scrollRIght = () => {
    if (cardsRef.current) {
      cardsRef.current.scrollTo({ left: cardsRef.current.scrollWidth, behavior: "smooth" });
      setShowRightButton(false);
      setTimeout(() => {
        setShowLeftButton(true);
      }, 200);
    }
  };
  const scrollLeft = () => {
    if (cardsRef.current) {
      cardsRef.current.scrollTo({ left: 0, behavior: "smooth" });
      setShowLeftButton(false);
      setTimeout(() => {
        setShowRightButton(true);
      }, 200);
    }
  };

  const isBrowser = typeof window !== "undefined";

  // Use the custom hook for tracking widget impressions
  const componentRef = useTrackWidgetImpression(
    {
      sectionName: `${heading}`,
      pageType: pageType,
      location: isBrowser ? window.location : null,
      config: qtState.config,
      threshold: 0.2,
    },
    { disabled: !isBrowser }
  );

  useEffect(() => {
    const container = cardsRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollLeft, clientWidth, scrollWidth } = container;
      if (scrollLeft === 0) {
        setShowRightButton(true);
        setShowLeftButton(false);
      }

      if (scrollLeft + clientWidth >= scrollWidth) {
        setShowRightButton(false);
        setShowLeftButton(true);
      }
    };
    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [cardsRef]);

  let isTracking = false;

  const handleWidgetClick = () => {
    if (isTracking) return;
    isTracking = true;

    setTimeout(() => {
      isTracking = false;
    }, 300);

    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, config, story);
      window.dataLayer.push({
        event: "dl_ranking_widget_click",
        link_text: buttonText.toLowerCase(),
        link_position: "-1",
        link_category: linkCategory?.toLowerCase(),
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(story?.["story-template"] || pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  return (
    <div
      styleName={`four-profile-cards-wrapper ${customStyleName} ${
        storyPage ? "four-profile-cards-wrapper-storyPage" : ""
      }`}
      ref={componentRef}
    >
      <div className="container" styleName="four-profile-cards">
        <div styleName="header">
          <div styleName="title">
            {!trendingPeople && (
              <img {...(storyPage ? { loading: "lazy" } : {})} styleName="header-logo" src={logoLink} alt="logo" />
            )}
            <Span customStyleName="ranking-story-span">{trendingPeople ? "Trending People" : `${heading} ${year}`}</Span>
          </div>

          {!trendingPeople && (
            <Link
              href={buttonLink}
              styleName="link"
              disableAjaxLinks={true}
              onMouseDown={handleWidgetClick}
              onTouchStart={handleWidgetClick}
            >
              <span styleName="link-text">{buttonText}</span>
              <SvgIconHandler type="arrow" width="24px" height="24px" viewBox="0 0 24 24" styleName="arrow-style" />
            </Link>
          )}
        </div>
        <div styleName="cards" ref={cardsRef}>
          {options.slice(0, 4)?.map((cardData, index) => (
            <ProfileCard
              showRanking={showRanking}
              entityData={cardData}
              key={`${cardData?.name}-${index}`}
              cdnImage={cdnImage}
              storyPage={storyPage}
              linkCategory={linkCategory}
              position={index + 1}
              story={story}
              config={config}
              trendingPeople={trendingPeople}
            />
          ))}
          {storyPage && showLeftButton && (
            <div styleName="nav-button back" onClick={() => scrollLeft()}>
              <span styleName="svg-back">
                <ArrowDashIcon height="26" width="26" viewBox="0 0 26 26" />
              </span>
            </div>
          )}
          {storyPage && showRightButton && (
            <div styleName="nav-button next" onClick={() => scrollRIght()}>
              <span styleName="svg-next">
                <ArrowDashIcon height="26" width="26" viewBox="0 0 26 26" />
              </span>
            </div>
          )}
        </div>

        {!trendingPeople && (
          <Link
            href={buttonLink}
            styleName="link"
            disableAjaxLinks={true}
            onMouseDown={handleWidgetClick}
            onTouchStart={handleWidgetClick}
          >
            <span styleName="link-text">{buttonText}</span>
            <SvgIconHandler type="arrow" width="24px" height="24px" viewBox="0 0 24 24" styleName="arrow-style" />
          </Link>
        )}
      </div>
    </div>
  );
};

FourProfileCards.defaultProps = {
  customStyleName: "",
  showRanking: true,
  options: [],
  heading: "",
  logoLink: "",
  buttonText: "",
  buttonLink: "",
};

FourProfileCards.propTypes = {
  customStyleName: string,
  showRanking: bool,
  storyPage: bool,
  options: arrayOf(
    shape({
      rank: string.isRequired,
      profileImg: string.isRequired,
      linkTo: string.isRequired,
      name: string.isRequired,
      designation: string.isRequired,
    })
  ).isRequired,
  heading: string,
  logoLink: string,
  buttonText: string,
  buttonLink: string,
  trendingPeople: bool,
};

export default FourProfileCards;
